import { athenaApi } from './athenaApi';

/**
 * Fully Dynamic RTK Query API for DynamicForm
 * 
 * This provides generic endpoints that work with ANY API route:
 * - Automatic Docker environment compatibility 
 * - Full RTK Query caching and performance
 * - Support for custom HTTP methods, headers, query params
 * - Zero maintenance - works with any endpoint without code changes
 */
export const dynamicFormApi = athenaApi.injectEndpoints({
  endpoints: (build) => ({
    // Generic GET with full URL support - works with any API endpoint
    fetchGeneric: build.query<any, { url: string; params?: Record<string, any>; headers?: Record<string, string> }>({
      query: ({ url, params, headers }) => ({ url, params, headers }),
      // Enable caching for form data fetches
      keepUnusedDataFor: 300, // 5 minutes
    }),

    // Generic mutation with full URL support - works with any API endpoint  
    postGeneric: build.mutation<
      any,
      { url: string; body?: any; method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE'; headers?: Record<string, string> }
    >({
      query: ({ url, body, method = 'POST', headers }) => ({ url, method, body, headers }),
      // Invalidate relevant caches based on URL to ensure proper list refreshes
      // This ensures FormLists automatically update without manual refresh after Save/Save&Submit
      invalidatesTags: (result, error, arg) => {
        const tags: any[] = [];
        
        // Extract form ID from URL for targeted cache invalidation
        // Matches patterns like /api/trip-reports/123 or /api/tars/456
        const idMatch = arg.url.match(/\/(trip-reports|tars|ctps|rips|pnrs)\/([a-f0-9-]+)/i);
        const formId = idMatch ? idMatch[2] : null;
        
        // Invalidate Trip Reports
        if (arg.url.includes('/trip-reports')) {
          tags.push('Trip Reports', 'TR (Trip Report)', 'Deliverables');
          if (formId && arg.method !== 'POST') {
            tags.push({ type: 'TR (Trip Report)', id: formId });
          }
          if (result?.trId) {
            tags.push({ type: 'TR (Trip Report)', id: result.trId });
          }
        }
        
        // Invalidate TARs
        if (arg.url.includes('/tars')) {
          tags.push('TAR (Travel Authorization Request)', 'Deliverables');
          if (formId && arg.method !== 'POST') {
            tags.push({ type: 'TAR (Travel Authorization Request)', id: formId });
          }
          if (result?.tarId) {
            tags.push({ type: 'TAR (Travel Authorization Request)', id: result.tarId });
          }
        }
        
        // Invalidate CTPs
        if (arg.url.includes('/ctps')) {
          tags.push('CTP (Consent to Purchase)', 'Deliverables');
          if (formId && arg.method !== 'POST') {
            tags.push({ type: 'CTP (Consent to Purchase)', id: formId });
          }
          if (result?.ctpId) {
            tags.push({ type: 'CTP (Consent to Purchase)', id: result.ctpId });
          }
        }
        
        // Invalidate RIPs
        if (arg.url.includes('/rips')) {
          tags.push('RIP (Request to Initiate Purchase)', 'Deliverables');
          if (formId && arg.method !== 'POST') {
            tags.push({ type: 'RIP (Request to Initiate Purchase)', id: formId });
          }
          if (result?.ripId) {
            tags.push({ type: 'RIP (Request to Initiate Purchase)', id: result.ripId });
          }
        }
        
        // Invalidate PNRs
        if (arg.url.includes('/pnrs')) {
          tags.push('PNR (Problem Notification Report)', 'Deliverables');
          if (formId && arg.method !== 'POST') {
            tags.push({ type: 'PNR (Problem Notification Report)', id: formId });
          }
          if (result?.pnrId) {
            tags.push({ type: 'PNR (Problem Notification Report)', id: result.pnrId });
          }
        }
        
        // Invalidate Attachments
        if (arg.url.includes('/attachments')) {
          tags.push('Attachment', 'Attachments');
        }
        
        // Invalidate Approval Queue for submit operations
        if (arg.url.includes('/submit')) {
          tags.push('Approval Queue');
        }
        
        return tags;
      },
    }),

    // Generic FormData upload with full URL support
    uploadFormData: build.mutation<
      any,
      { url: string; formData: FormData; method?: 'POST' | 'PUT' | 'PATCH'; headers?: Record<string, string> }
    >({
      query: ({ url, formData, method = 'POST', headers }) => ({ url, method, body: formData, headers }),
      // Invalidate relevant caches for file uploads to ensure FormLists refresh
      invalidatesTags: (result, error, arg) => {
        const tags: any[] = [];
        
        // Extract form ID from URL for targeted cache invalidation
        const idMatch = arg.url.match(/\/(trip-reports|tars|ctps|rips|pnrs)\/([a-f0-9-]+)/i);
        const formId = idMatch ? idMatch[2] : null;
        
        // Invalidate Trip Reports
        if (arg.url.includes('/trip-reports')) {
          tags.push('Trip Reports', 'TR (Trip Report)', 'Deliverables');
          if (formId) {
            tags.push({ type: 'TR (Trip Report)', id: formId });
          }
        }
        
        // Invalidate TARs
        if (arg.url.includes('/tars')) {
          tags.push('TAR (Travel Authorization Request)', 'Deliverables');
          if (formId) {
            tags.push({ type: 'TAR (Travel Authorization Request)', id: formId });
          }
        }
        
        // Invalidate CTPs
        if (arg.url.includes('/ctps')) {
          tags.push('CTP (Consent to Purchase)', 'Deliverables');
          if (formId) {
            tags.push({ type: 'CTP (Consent to Purchase)', id: formId });
          }
        }
        
        // Invalidate RIPs
        if (arg.url.includes('/rips')) {
          tags.push('RIP (Request to Initiate Purchase)', 'Deliverables');
          if (formId) {
            tags.push({ type: 'RIP (Request to Initiate Purchase)', id: formId });
          }
        }
        
        // Invalidate PNRs
        if (arg.url.includes('/pnrs')) {
          tags.push('PNR (Problem Notification Report)', 'Deliverables');
          if (formId) {
            tags.push({ type: 'PNR (Problem Notification Report)', id: formId });
          }
        }
        
        // Invalidate Attachments
        if (arg.url.includes('/attachments')) {
          tags.push('Attachment', 'Attachments');
        }
        
        return tags;
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyFetchGenericQuery,
  usePostGenericMutation,
  useUploadFormDataMutation,
} = dynamicFormApi;
